import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { Link as RouterLink } from 'react-router';
import './Auth.scss';

const signInBenefits = [
  {
    icon: <LocalShippingRoundedIcon fontSize='small' />,
    title: 'Track every order',
    text: 'View delivery progress, order history, and address details from one place.',
  },
  {
    icon: <FavoriteRoundedIcon fontSize='small' />,
    title: 'Save your favorites',
    text: 'Keep your preferred papads, combos, and repeat buys ready for quick checkout.',
  },
  {
    icon: <RedeemRoundedIcon fontSize='small' />,
    title: 'Use offers faster',
    text: 'Apply coupons, view exclusive deals, and manage rewards with less friction.',
  },
];

export default function SignIn() {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Box className='auth-page'>
      <Box className='auth-shell'>
        <Box className='auth-panel auth-panel--brand'>
          <Chip className='auth-chip' icon={<LockRoundedIcon />} label='Customer Sign In' />

          <Typography className='auth-kicker'>Pappad made personal</Typography>
          <Typography variant='h2' className='auth-title'>
            Welcome back to your favorite snacks and repeat orders.
          </Typography>
          <Typography className='auth-subtitle'>
            Sign in with your email or mobile number and continue shopping with
            saved addresses, order tracking, and faster checkout.
          </Typography>

          <Stack spacing={2.25} className='auth-benefits'>
            {signInBenefits.map((item) => (
              <Box key={item.title} className='auth-benefit'>
                <Box className='auth-benefitIcon'>{item.icon}</Box>
                <Box>
                  <Typography className='auth-benefitTitle'>
                    {item.title}
                  </Typography>
                  <Typography className='auth-benefitText'>
                    {item.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box className='auth-panel auth-panel--form'>
          <Box className='auth-formWrap'>
            <Typography variant='h4' className='auth-formTitle'>
              Sign in
            </Typography>
            <Typography className='auth-formSubtitle'>
              Enter your email or mobile number and your password to continue.
            </Typography>

            <Alert severity='info' className='auth-alert'>
              Use the same account for favorites, orders, and saved addresses.
            </Alert>

            <Stack component='form' spacing={2.25} className='auth-form'>
              <TextField
                fullWidth
                label='Email or mobile'
                placeholder='name@email.com or 9876543210'
              />

              <TextField
                fullWidth
                label='Password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Enter your password'
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box className='auth-inlineRow'>
                <Link component={RouterLink} to='/signup' underline='hover' className='auth-link'>
                  New here? Create account
                </Link>
                <Link href='/' underline='hover' className='auth-link'>
                  Forgot password?
                </Link>
              </Box>

              <Button variant='contained' size='large' disableElevation className='auth-submit'>
                Sign in
              </Button>
            </Stack>

            <Divider className='auth-divider'>Or continue with</Divider>

            <Stack className='auth-socials'>
              <Button variant='outlined' className='auth-socialButton'>
                <Box className='auth-socialMark auth-socialMark--google'>G</Box>
                Continue with Google
              </Button>
              <Button variant='outlined' className='auth-socialButton'>
                <Box className='auth-socialMark auth-socialMark--facebook'>f</Box>
                Continue with Facebook
              </Button>
              <Button variant='outlined' className='auth-socialButton'>
                <Box className='auth-socialMark auth-socialMark--apple'>A</Box>
                Continue with Apple
              </Button>
            </Stack>

            <Typography className='auth-footerText'>
              By signing in, you can access your orders, wishlist, coupons, and
              saved delivery addresses anytime.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
